import { ConfigService } from '../config/config.service';
import { DataService } from '../data/data.service';
import { DocumentUpdateService } from '../document/document-update.service';
import { OfflineStatusService } from '../offline-status/offline-status.service';
import { RestDataService } from '../data/rest-data.service';
import { UnsavedDocumentService } from '../document/unsaved-document.service';
import { UrlBasePathUtil } from '../../utils/UrlBasePathUtil';
import { SharedConstants } from '@formbird/types';
import { Injectable } from '@angular/core';
import * as io from 'socket.io-client';
import { VersionService } from '../version/version.service';
import { ClientAccessService } from '../access/client-access.service';
import { LoggedInUserService } from '../user/logged-in-user.service';
import { ClientConstants } from '../../constants/ClientConstants';

const logger = console;

const urlBasePath = UrlBasePathUtil.basePathWithSlashPrefix;
const path = { path: 'socket.io' };

const WEB_SOCKET_RECONNECT_TIME = 1000;

@Injectable({
  providedIn: 'root'
})
export class WebSocketService {

  private socket;

  constructor(
    private offlineStatusService: OfflineStatusService,
    private unsavedDocumentService: UnsavedDocumentService,
    private dataService: DataService,
    private restDataService: RestDataService,
    private documentUpdateService: DocumentUpdateService,
    private configService: ConfigService,
    private clientAccessService: ClientAccessService,
    private loggedInUserService: LoggedInUserService

  ) {
  }

  private getTransports() {
    if (this.configService.clientConfig().socketIO && this.configService.clientConfig().socketIO.transports) {
      return this.configService.clientConfig().socketIO.transports;
    }
    return null;
  }

  /**
   * cache any documents saved while offline. This can either be when the browser was closed or the device was offline
   */
  private loadDocumentsSavedWhileOffline() {

    const _self = this;

    logger.info('Device has come online or app has been started. Loading any data saved while offline');

    const isCacheEnabled = _self.offlineStatusService.isCachingEnabled();
    const isCacheLoading = _self.offlineStatusService.isCacheLoading();
    const isDataCacheCompleted = _self.offlineStatusService.isDataCachingCompleted();
    if (!isCacheEnabled || (isCacheEnabled && isCacheLoading && !isDataCacheCompleted)) {
      //Should not load and update any data cache
      //if the initial data caching process has not completed yet or caching is not enabled
      return;
    }

    const promise: Promise<any> = _self.dataService.loadDataCache(false);

    promise.then(updateCachedDocuments);

    async function updateCachedDocuments(newDocuments) {

      if (newDocuments) {
        for (let i = 0; i < newDocuments.length; i++) {
          let newDocument = newDocuments[i];
          await _self.documentUpdateService.updateVersions([newDocument]);
        }
      }

      return newDocuments;
    }
  }

  start() {

    if (this.socket){
        return;
    };
    
    logger.info('Starting websocket...');

    const _self = this;

    const options = {
      resource: path,
      path: null,
      transports: ["websocket"]
    };

    const transports = _self.getTransports();
    if (transports) {
      options.transports = transports;
    }

    options.path = urlBasePath + '/socket.io';
    _self.socket = io.connect('/', options);

    function startReconnect() {
      if (!_self.socket.connected) {
        setTimeout(doReconnect, WEB_SOCKET_RECONNECT_TIME);
      }
    }

    function doReconnect() {
      if (!_self.socket.connected) {
        startReconnect();
        _self.socket.connect();
      }
    }

    _self.socket.on(SharedConstants.WEB_SOCKET_MESSAGE_SAVE_DOCUMENT, function (docInfo) {

      const documentId = docInfo?.documentId;
      const loggedUserId = _self.loggedInUserService.getUserId();

      // Ensure that if the current user's account document is updated, the components
      // depending on LoggedInUserService are informed. Ex. The Home button
      const isUserProfileDocument = documentId === loggedUserId;

      if (_self.offlineStatusService.isCachingEnabled()
        || _self.unsavedDocumentService.documentIsLoaded(documentId)
        || isUserProfileDocument
      ) {

        return _self.restDataService.loadVersions(docInfo.versionIds).then(async function successFunc(versions) {

          await _self.documentUpdateService.updateVersions(versions);

          const isCacheEnabled = _self.offlineStatusService.isCachingEnabled();
          const isCacheLoading = _self.offlineStatusService.isCacheLoading();
          const isDataCacheCompleted = _self.offlineStatusService.isDataCachingCompleted();
          const isRunningCacheClear =_self.loggedInUserService.getUserConfigItem('isRunningCacheClear');
          if (isCacheEnabled && !isCacheLoading && isDataCacheCompleted && !isRunningCacheClear) {

            const hasOfflineAccess = _self.clientAccessService.checkAccess(docInfo, SharedConstants.OPERATION_TYPE_OFFLINE);
            if (hasOfflineAccess) {
              _self.offlineStatusService.addToMaxCacheCount(1);
            }

            // pass versions in. If it was not fetched already
            _self.dataService.cacheDocument(docInfo, versions).then(
              function successFunc(versions) {
                logger.info("Cached pushed document for offline use: " + documentId);
              },
              function errorFunc(err) {
                logger.info("Not found document from received pushed versions: " + documentId, err);
              }
            );
          }
        }, function errorFunc(err) {
          logger.error("Error loading pushed document: " + err.message);
        });
      }
    });

    _self.socket?.on('reconnect', function (attempt) {
      _self.loadDocumentsSavedWhileOffline();
    });

    _self.socket?.on('connect', async () => {
      logger.info('Connected to notification socket. Setting status to connected.');

      const connectedToServer = await _self.offlineStatusService.checkServerConnectionStatus();
      if (connectedToServer) {
        _self.offlineStatusService.setConnected();
        _self.loadDocumentsSavedWhileOffline();
        VersionService.checkServerVersion();
        _self.documentUpdateService.checkDocumentsCreated();
         _self.emit(ClientConstants.WEB_SOCKET_STATUS, true);
      }
    });

    _self.socket?.on('connect_error', function (data) {

      logger.info('Error in notification socket. Setting status to disconnected');

      _self.offlineStatusService.setDisconnected();
      // _self.utilService.scopeApply(_self.$rootScope);

    });

    _self.socket?.on('disconnect', function (data) {

      logger.info('Disconnected from notification socket. Setting status to disconnected');

      _self.offlineStatusService.setDisconnected();

      startReconnect();

    });

    _self.socket?.on(SharedConstants.WEB_SOCKET_MESSAGE_PASSWORD_RESET, function (userId) {
      _self.emit( SharedConstants.PASSWORD_RESET, userId);
    });
  }

  /**
   * end the websocket session
   */
  end() {
    // disconnect from the socket. No callback is fired on doing this. A disconnect event is fired but only if the socket is
    // currently connected

    if (this.socket) {
      this.socket.disconnect();
    }

  }

  emit(eventName, data) {
    this.socket?.emit(eventName, data);
  }

  addEventListener(eventName, eventHandlerFunc) {
    this.socket?.on(eventName, eventHandlerFunc);
  }

  removeEventListener(eventName) {
    this.socket?.off(eventName);
  }
}
